import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { cloneDeep } from 'lodash-es';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonSize, ButtonStyle } from '../../aero/aero-button/aero-button.component';
import {
  DeleteAccountDialogService,
} from '../account-settings-dialog/delete-account-dialog/delete-account-dialog.service';
import { TranslationService } from '../../common/translation.service';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';
import { AeroDialogService } from '../../aero/aero-dialog/aero-dialog.service';
import { AccountSettingsDialogService } from '../account-settings-dialog/account-settings-dialog.service';
import { AccountSettingsService } from './account-settings.service';
import { JoinGroupComponent } from './join-group.component';
import { ParentAccountsComponent } from './parent-accounts.component';
import { AddCouponComponent } from './add-coupon.component';
import { AppConfigService } from '../../common/services/app-config/app-config.service';

/**
 * Presents and manages the signed-in user's account settings.
 *
 * Coordinates account profile and preference updates, connected services,
 * notifications, billing, memberships, and account-related dialogs. The
 * component also synchronizes successful account changes with the auth service.
 */
@Component({
  selector: 'bulb-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss'],
  standalone: false,
})
export class AccountSettingsComponent implements OnInit {

  private readonly bulbAppUrl = 'https://my.bulbapp.com/account-deleted/';

  public ButtonSize = ButtonSize;

  public ButtonStyle = ButtonStyle;

  public account: any;

  public storedAccount: any;

  public activeSection = 'settings';

  public language = '';

  public languages: any[] = [];

  public emailfrequencyType = ['IMMEDIATELY', 'DAILY', 'WEEKLY'];

  public orgName: string;

  public connectionProviders: { key: string; label: string }[] = [];

  private readonly connectionAuthTypeAliases: Record<string, string> = { mo365: 'microsoft' };

  private readonly connectableProviders = ['google', 'mo365', 'clever'];

  private readonly connectionIdentifierFields = ['email', 'uniqueName', 'displayName', 'sourceId'];

  public smsNotificationOptionsEnabled = false;

  public pushNotificationOptionsEnabled = false;

  public groupNotifications = [
    { field: 'groupJoined', code: 'notification_settings_group_owner_groupJoined', show: () => true },
    {
      field: 'pageOrCollectionPublished',
      code: 'notification_settings_group_member_pageOrCollectionPublished',
      show: (group) => group.isOwner,
    },
  ];

  public loading = false;

  public mfaQRCode: string = '';

  /** Creates the component and injects account, dialog, routing, and API services. */
  constructor(
    private http: HttpClient,
    private translation: TranslationService,
    private deleteAccountDialog: DeleteAccountDialogService,
    private dynamicDialogRef: DynamicDialogRef,
    private dialogService: AeroDialogService,
    private accountDialogService: AccountSettingsDialogService,
    private accountSettingsService: AccountSettingsService,
    private appConfig: AppConfigService,
    @Inject(BULB_BUSY_COVER_SERVICE) private busyService: any,
    @Inject('BulbAuthService') private authService: any,
    @Inject('$translate') private translate: any,
    @Inject('$state') private state: any,
    @Inject('BulbErrorService') private errorService: any,
    @Inject('PushNotificationService') private pushNotificationService: any,
  ) {
  }

  /** Loads the current account state and handles settings-page entry callbacks. */
  ngOnInit(): void {
    this.account = this.authService.currentUser;
    this.storedAccount = cloneDeep(this.account);
    this.language = this.translate.localeToString();
    this.languages = this.translate.getLanguagesList();
    this.orgName = this.account.groupMemberships.find((group) => group.type === 'ORGANIZATION')?.displayName;

    // 2. Initialize exactly once to prevent Angular DOM thrashing which breaks clicks
    this.initConnectionProviders();

    // Check if the MacMillan book dialog should be displayed
    this.checkMacMillanBookDialog();

    // Check for Badgr OAuth callback code
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      this.handleBadgrOAuth(code);
      // Remove the code from the URL without reloading the page
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Check for duplicate email OAuth connection errors
    this.checkDuplicateEmailError();

    this.smsNotificationOptionsEnabled = this.appConfig.features.SMSNotificationOptionsEnabled;
    this.pushNotificationOptionsEnabled = this.appConfig.features.PushNotificationOptionsEnabled;
  }

  /** Exchanges a Badgr authorization code and displays the connection result. */
  handleBadgrOAuth(authCode: string): void {
    const hideBusy = this.busyService.showBusy();

    this.http.post(`accounts/${this.account.id}/badges/auth`, { authCode }).subscribe({
      next: () => {
        hideBusy();
        this.refreshAccount(); // Refresh account to update the hasBadges property in the UI

        // Show success modal reconstructing the old nested template
        this.dialogService.showDialog({
          icon: 'fas fa-badge-check',
          iconClass: 'aero-highlight',
          title: this.translation.translateInstant('badgr_link_successful'),
          detail: `
            <div class="bulb-badge-success-modal">
              <div>${this.translation.translateInstant('badgr_success_modal1')}</div>
              <div><i class="fas fa-medal"></i> ${this.translation.translateInstant('badgr_success_modal2')}</div>
              <br/>
              <b>
                <a href="/${this.account.username}" class="bulb-badge-redirect-hyperlink">
                  ${this.translation.translateInstant('badgr_view_profile_page')}
                </a>
              </b>
            </div>
          `,
          primaryLabel: this.translation.translateInstant('button_ok'),
          showSecondary: false,
        });
      },
      error: () => {
        hideBusy();
        this.dialogService.showDialog({
          title: this.translation.translateInstant('server_error_title'),
          detail: this.translation.translateInstant('server_error_body'),
          primaryLabel: this.translation.translateInstant('button_ok'),
          showSecondary: false,
        });
      },
    });
  }

  /** Confirms and removes the account's Badgr connection. */
  disconnectBadgr(): void {
    this.dialogService.showDialog({
      icon: 'fas fa-exclamation-triangle',
      iconClass: 'aero-alert',
      title: this.translation.translateInstant('badge_disconnect_modal_title'),
      detail: this.translation.translateInstant('badge_disconnect_modal_body'),
      primaryLabel: this.translation.translateInstant('account_settings_disconnect'),
      primaryStyle: this.ButtonStyle.ALERT,
      showSecondary: true,
      onPrimary: () => {
        // Leverages the existing `request` wrapper which automatically handles busy state and refreshes the user
        this.request('delete', `accounts/${this.account.id}/badges`);
      },
    });
  }

  /** Builds the available connection-provider list from enabled integrations. */
  private initConnectionProviders(): void {
    const providers = [
      { key: 'google', label: 'account_settings_google' },
      { key: 'mo365', label: 'account_settings_microsoft' },
    ];

    if (this.appConfig?.features?.cleverIntegrationEnabled) {
      providers.push({ key: 'clever', label: 'account_settings_clever' });
    }

    providers.push({ key: 'classLink', label: 'account_settings_classLink' });

    if (this.appConfig?.features?.schoologyIntegrationEnabled) {
      providers.push({ key: 'schoology', label: 'account_settings_schoology' });
    }

    providers.push({ key: 'canvas', label: 'account_settings_canvas' });

    this.connectionProviders = providers;
  }

  /** Redirects to the selected provider's authentication flow. */
  connect(type: string): void {
    if (this.isOrgAdminSwitchUserClient) return;

    window.location.href = `/b/auth/${type}?entryMode=connect`
      + `&targetUrl=${encodeURIComponent('/b/account/settings')}`;
  }

  /** Confirms and removes a provider connection, protecting sync-managed links. */
  disconnect(type: string, connection: any): void {
    if (this.isOrgAdminSwitchUserClient) return;

    if (connection.createdVia === 'SYNC') {
      this.dialogService.showDialog({
        icon: 'fas fa-exclamation-triangle',
        iconClass: 'aero-alert',
        title: this.translation.translateInstant(`account_settings_${type}_disconnect_confirm_title`),
        detail: this.translation.translateInstant('account_settings_cannot_disconnect_connection_created_via_sync'),
        primaryLabel: this.translation.translateInstant('button_ok'),
        showSecondary: false,
      });
      return;
    }

    // This logic handles multiple connections perfectly.
    // It only triggers account deletion if it's the ONLY connection left on the whole account.
    const totalConnectionTypes = Object.keys(this.account?.connections || {}).length;
    const currentTypeConnections = Object.keys(this.account?.connections?.[type] || {}).length;
    const onlyConnection = totalConnectionTypes === 1 && currentTypeConnections === 1;

    const emailMissing = !this.account.emails || this.account.emails.length === 0;
    const emailUnverified = !emailMissing && this.account.emails.filter((e: any) => e.verified).length === 0;

    let detailHtml = `<p>${this.translation.translateInstant(`account_settings_${type}_disconnect_confirm_body`)}</p>`;

    if (onlyConnection || emailMissing || emailUnverified) {
      detailHtml += '<ul>';
      if (onlyConnection) {
        detailHtml += `<li>${this.translation.translateInstant(
          'account_settings_disconnect_warning_no_password',
        )}</li>`;
      }
      if (emailMissing) {
        detailHtml += `<li>${this.translation.translateInstant(
          'account_settings_disconnect_warning_no_email',
        )}</li>`;
      }
      if (!emailMissing && emailUnverified) {
        detailHtml += `<li>${this.translation.translateInstant(
          'account_settings_disconnect_warning_no_verified_email',
        )}</li>`;
      }
      detailHtml += '</ul>';
    }

    if (onlyConnection) {
      detailHtml += `<p>${this.translation.translateInstant('account_settings_disconnect_warning_delete')}</p>`;
    }

    this.dialogService.showDialog({
      icon: 'fas fa-exclamation-triangle',
      iconClass: 'aero-alert',
      title: this.translation.translateInstant(`account_settings_${type}_disconnect_confirm_title`),
      detail: detailHtml,
      primaryLabel: this.translation.translateInstant('account_settings_disconnect_confirm_button'),
      primaryStyle: this.ButtonStyle.ALERT,
      showSecondary: true,
      onPrimary: () => {
        if (onlyConnection) {
          this.deleteAccountApi();
        } else {
          this.request('delete', `accounts/${this.account.id}/connections/${connection.id}`);
          if (Array.isArray(this.account?.connections?.[type])) {
            this.account.connections[type] = this.account.connections[type].filter(
              (c: any) => c.id !== connection.id,
            );
          }
        }
      },
    });
  }

  /** Returns the authenticated user from the auth service. */
  get currentUser(): any {
    return this.authService.currentUser;
  }

  /** Returns storage usage capped at the account's storage limit. */
  get storageUsed(): number {
    const feature = this.account?.features?.StorageFeature;
    return feature ? Math.min(feature.currentUsedStorageBytes || 0, feature.maxStorageBytes || 1) : 0;
  }

  /** Returns the account's storage limit, or the minimum display fallback. */
  get storageMax(): number {
    return this.account?.features?.StorageFeature?.maxStorageBytes || 1;
  }

  /** Returns the number of pages currently published by the account. */
  get sharedPages(): number {
    return this.account?.metrics?.numPublishedPages || 0;
  }

  /** Returns the account's published-page limit. */
  get sharedPagesMax(): number {
    return this.account?.features?.PublishingFeature?.maxPublishedPages || 1;
  }

  /** Returns uppercase initials for the account avatar. */
  get initials(): string {
    const first = this.account?.firstName?.charAt(0) || '';
    const last = this.account?.lastName?.charAt(0) || '';
    return (first + last || this.account.username?.slice(0, 2) || '?').toUpperCase();
  }

  /** Returns the account type, with a display fallback when unavailable. */
  get accountType(): string {
    return this.account?.type || 'bulb';
  }

  /** Returns the localized-facing display label for the account type. */
  get accountTypeLabel(): string {
    const labels = {
      ORG: 'bulbEd',
      ORG_DIRECT: 'bulbEd Direct',
      ORG_DIRECT_SUBSCRIPTION: 'bulbEd Direct+',
      BULB_SUBSCRIPTION: 'bulb+',
      SENIOR_GIFT: 'Senior',
      MACMILLAN: 'bulb+ NextStation',
      MARISTA: 'bulb+ MaristaNS',
      TRANSFOR_ME: 'bulb+ Transforme',
      TEACHER: 'Teacher - bulb+',
    };
    const accountType = this.account?.type;
    return Object.prototype.hasOwnProperty.call(labels, accountType) ? labels[accountType] : 'bulb';
  }

  /** Returns the current page origin for account-setting links. */
  public readonly urlOrigin = window.location.origin;

  /** Indicates whether the account's resume is enabled. */
  get isResumeEnabled(): boolean {
    return !!this.account?.features?.ResumeFeature?.resumeEnabled;
  }

  /** Indicates whether the public profile is indexed for web search. */
  get isPublicProfileEnabled(): boolean {
    return !!this.account?.features?.WebSearchIndexingAccountFeature?.enabled;
  }

  /** Indicates whether family-account functionality is enabled. */
  get isFamilyFeatureEnabled(): boolean {
    return !!this.account?.features?.FamilyFeature?.familyAccountEnabled;
  }

  /** Indicates whether data-entry protection permits profile-name changes. */
  get canUpdateNames(): boolean {
    return !this.currentUser?.features?.DataEntryProtectionFeature?.enabled;
  }

  /** Indicates whether data-entry protection permits adding email addresses. */
  get canAddEmail(): boolean {
    return !this.currentUser?.features?.DataEntryProtectionFeature?.enabled;
  }

  /** Returns the translated action or restriction label for resume settings. */
  get resumeLabel(): string {
    if (this.account.features.ResumeFeature.disabledByOrg) {
      return this.translation.translateInstant('account_settings_resume_feature_not_enabled');
    }
    return this.translation.translateInstant(!this.isResumeEnabled ? 'account_settings_show' : 'account_settings_hide');
  }

  /** Saves changed profile fields and preferences to the account API. */
  update(): void {
    const patch: any = { prefs: this.storedAccount.prefs };

    ['username', 'firstName', 'lastName', 'displayName'].forEach((key) => {
      if (this.account[key] !== this.storedAccount[key]) patch[key] = (this.account[key] || '').trim();
    });

    if (this.account.language !== this.storedAccount.language) patch.language = this.account.language;
    if (this.account.password) patch.password = this.account.password;

    if (this.account.prefs?.marketing?.optIn !== this.storedAccount.prefs?.marketing?.optIn) {
      patch.prefs.marketing.optIn = this.account.prefs.marketing.optIn;
    }

    if (this.account.prefs?.importingGoogleAssetAsPdfEnabled
      !== this.storedAccount.prefs?.importingGoogleAssetAsPdfEnabled) {
      patch.prefs.importingGoogleAssetAsPdfEnabled = this.account.prefs.importingGoogleAssetAsPdfEnabled;
    }

    if (patch.email === '' && !this.account.connections?.google && this.account.type === 'FREE') {
      this.dialogService.showDialog({
        icon: 'fas fa-exclamation-triangle',
        iconClass: 'aero-alert',
        title: this.translation.translateInstant('account_settings_verify_email_title'),
        detail: this.translation.translateInstant('account_settings_verify_email_delete_body'),
        primaryLabel: this.translation.translateInstant('button_continue'),
        showSecondary: true,
        onPrimary: () => {
          this.refreshAccount();
        },
        onSecondary: () => {
          // If user cancels, discard changes so form fields correctly reflect the stored account
          this.account = cloneDeep(this.storedAccount);
        },
        onClose: () => {
          this.account = cloneDeep(this.storedAccount);
        },
      });
      return;
    }

    this.request('patch', `accounts/${this.account.id}`, patch);
  }

  /** Selects the notifications section of the settings screen. */
  selectNotifications(): void {
    this.activeSection = 'notifications';
  }

  /** Opens information about the Badgr badge integration. */
  openBadgesInfoModal(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    this.dialogService.showDialog({
      icon: 'fas fa-medal',
      iconClass: 'aero-highlight', // Gives the icon the nice brand color
      title: this.translation.translateInstant('badgr_about'),
      detail: this.translation.translateInstant('badgr_info_modal'),
      primaryLabel: this.translation.translateInstant('onboarding_got_it_button'),
      showSecondary: false,
    });
  }

  /** Updates the account language and reloads the page when it changes. */
  changeLanguage(event: Event): void {
    const value = (event as any).selectedOption || (event.target as HTMLSelectElement).value;
    if (this.account.language.toLowerCase() !== value.toLowerCase()) {
      this.language = this.translate.localeToString();
      this.account.language = value;
      this.update();
      window.location.reload();
    }
  }

  /** Deletes the account and redirects to the Bulb home page on success. */
  deleteAccountApi() {
    this.http.delete(`accounts/${this.account.id}`)
      .toPromise()
      .then(() => {
        window.location.href = this.bulbAppUrl;
      })
      .catch((error) => {
        this.errorService.showError(null, error);
      });
  }

  /** Persists the updated marketing-consent preference. */
  toggleMarketing(): void {
    this.update();
  }

  /** Enables or disables the account resume feature. */
  toggleResume(): void {
    const action = this.account.features.ResumeFeature.resumeEnabled ? 'disable' : 'enable';
    this.request('patch', `accounts/${this.account.id}/resume/${action}`);
  }

  /** Toggles public-profile indexing for the account. */
  togglePublicProfile(): void {
    const enabled = !this.account.features.WebSearchIndexingAccountFeature.enabled;
    this.request('patch', `accounts/${this.account.id}/webSearchIndexing/${enabled}`);
  }

  /** Opens the account-deletion confirmation flow. */
  deleteAccount(): void {
    this.deleteAccountDialog.showDialog(!this.account.passwordNotStored, this.account.username, this.account.id);
  }

  /** Indicates whether the account belongs to an organization. */
  get isOrganizationMember(): boolean {
    return this.account?.groupMemberships?.some((group: any) => group.type === 'ORGANIZATION') ?? false;
  }

  /** Returns organization-related memberships, excluding classes and custom groups. */
  get filteredGroupMemberships(): any[] {
    return this.account?.groupMemberships?.filter(
      (group: any) => group.type !== 'OTHER' && group.type !== 'CLASS',
    ) || [];
  }

  /** Navigates to the selected organization's management page. */
  goToOrganization(groupId: string): void {
    this.state.go('organization', { id: groupId });
  }

  /** Opens the appropriate billing-management destination for this account. */
  openBilling(): void {
    if (this.account.billing?.managedByApple === true) {
      this.http.post('billing/apple/refresh', {}).subscribe({
        next: () => {
          window.location.href = 'https://support.apple.com/en-us/HT202039';
        },
        error: (error) => this.errorService.showError(null, error),
      });
      return;
    }
    this.http.post('billing/portal', {}).subscribe({
      next: (response: any) => {
        window.location.href = response.url;
      },
      error: (error) => this.errorService.showError(null, error),
    });
  }

  /** Displays account billing status and the available upgrade or manage action. */
  showBillingDialog(): void {
    const billing = this.account.billing || {};
    const renewalDate = this.billingRenewalDate(billing);
    const canUpgrade = this.account.type === 'FREE' && this.appConfig.stripe?.enabled === true;
    const detailLines = [
      `${this.translation.translateInstant('account_settings_type')}: <strong>${this.accountTypeLabel}</strong>`,
    ];

    if (billing.status === 'CANCELED' && renewalDate) {
      detailLines.push(
        this.translation.translateInstant('account_settings_canceled').replace('{{date}}', renewalDate),
      );
    } else if (billing.status === 'TRIAL' && renewalDate) {
      detailLines.push(
        `${this.translation.translateInstant('account_settings_trial_ends')}: <strong>${renewalDate}</strong>`,
      );
    } else if (billing.status === 'ACTIVE' && renewalDate) {
      detailLines.push(
        `${this.translation.translateInstant('account_settings_renewal')}: <strong>${renewalDate}</strong>`,
      );
    }

    if (billing.status === 'PAST_DUE') {
      detailLines.push(this.translation.translateInstant('account_settings_past_due_text'));
    }

    this.dialogService.showDialog({
      icon: 'fas fa-credit-card',
      iconClass: 'aero-highlight',
      title: this.translation.translateInstant('account_settings_account_type_billing'),
      detail: detailLines.join('<br>'),
      primaryLabel: this.translation.translateInstant(
        canUpgrade ? 'account_settings_upgrade' : 'account_settings_manage',
      ),
      showSecondary: false,
      showFooter: canUpgrade || this.canManageBilling,
      onPrimary: () => (canUpgrade ? this.state.go('planlist') : this.openBilling()),
    });
  }

  /** Indicates whether the current billing status supports management. */
  get canManageBilling(): boolean {
    return ['ACTIVE', 'CANCELED'].includes(this.account?.billing?.status);
  }

  /** Formats the applicable billing period end date for display. */
  private billingRenewalDate(billing: any): string {
    let periodEnds = billing.periodEnds || billing.appleBilling?.periodEnds;
    if (billing.status === 'CANCELED' || billing.status === 'TRIAL') {
      periodEnds = billing.periodEnds;
    } else if (billing.managedByApple === true) {
      periodEnds = billing.appleBilling?.periodEnds || billing.periodEnds;
    }
    if (!periodEnds) return '';
    const date = new Date(periodEnds);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(this.translate.localeToString(), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(periodEnds));
  }

  /** Prompts for an email address and submits it for addition to the account. */
  addEmail(): void {
    this.dialogService.showDialog({
      title: this.translation.translateInstant('account_settings_email'),
      primaryLabel: this.translation.translateInstant('button_ok'),
      showSecondary: true,
      dialogInput: {
        inputLabel: this.translation.translateInstant('account_settings_email'),
        onInputChange: (email: string) => {
          if (email) this.request('post', `accounts/${this.account.id}/emails`, { email });
        },
      },
    });
  }

  /** Sends a verification message to an account email address. */
  verifyEmail(_event: Event, email: string): void {
    this.request('post', `accounts/${this.account.id}/sendEmailVerification`, { email });
  }

  /** Removes an email address from the account. */
  removeEmail(_event: Event, email: string): void {
    this.request('delete', `accounts/${this.account.id}/emails?email=${email}`);
  }

  /** Checks whether the supplied address is the account's default email. */
  isDefaultEmail(email: any): boolean {
    return email.email === this.currentUser.defaultEmail;
  }

  /** Makes a verified email address the account's default address. */
  setDefaultEmail(event: Event, email: string, verified: boolean): void {
    if (verified) this.request('post', `accounts/${this.account.id}/setDefaultEmail`, { email });
  }

  /** Checks whether an address can be removed while retaining a verified address. */
  canRemoveEmail(email: string): boolean {
    const hasOtherVerifiedEmail = this.account.emails
      .some((item) => item.email !== email && item.verified);
    return !this.isDefaultEmail({ email }) && hasOtherVerifiedEmail;
  }

  /** Maps a connection provider key to its authentication route name. */
  connectionAuthType = (type: string): string => this.connectionAuthTypeAliases[type] || type;

  /** Checks whether the provider supports a user-initiated connection. */
  canConnect = (type: string): boolean => this.connectableProviders.includes(type);

  /** Returns the account's email records, or an empty list. */
  get emails(): any[] {
    return this.account?.emails || [];
  }

  /** Reads a notification preference value. */
  notificationValue(channel: string, key: string): boolean {
    return !!this.account.notificationPref?.[channel]?.[key];
  }

  /** Updates a notification preference, enforcing premium-feature requirements. */
  setNotification(channel: string, key: string, valueOrEvent?: boolean | { target?: { checked: boolean } }): void {
    const willBeEnabled = typeof valueOrEvent === 'boolean'
      ? valueOrEvent
      : !this.account.notificationPref[channel][key];
    const event = typeof valueOrEvent === 'object' ? valueOrEvent : undefined;

    if ((key === 'commentsReply' || key === 'newComments') && willBeEnabled) {
      if (!this.authService.currentUser.features?.CommentingFeature?.enabled) {
        this.account.notificationPref[channel][key] = false;
        if (event?.target) {
          event.target.checked = false;
        }
        const isSchoolDirect = this.authService.currentUser.bulbSchoolDirectMemberships > 0;
        this.dialogService.showDialog({
          svgIconName: 'b-plus-icon',
          iconClass: 'aero-highlight aero-bulb-plus-icon',
          title: this.translation.translateInstant('notification_feature_check_title'),
          detail: this.translation.translateInstant(isSchoolDirect
            ? 'notification_feature_check_content_bulbSchoolDirect'
            : 'notification_feature_check_content'),
          primaryLabel: this.translation.translateInstant('button_ok'),
          showSecondary: false,
        });
        return; // Abort API call
      }
    }

    if (channel === 'pushNotification' && key === 'enabled' && willBeEnabled) {

      // Check if the account actually has the Push feature enabled at the billing/org level
      if (!this.account.features?.PushFeature?.enabled) {
        if (event?.target) {
          event.target.checked = false;
        }
        this.account.notificationPref.pushNotification.enabled = false;

        // Determine which upsell copy to show based on group memberships
        const isSchoolDirect = this.authService.currentUser.bulbSchoolDirectMemberships > 0;
        const detailCode = isSchoolDirect
          ? 'notification_feature_check_content_bulbSchoolDirect'
          : 'notification_feature_push_modal_content';

        // Show Premium Feature Upsell Modal
        this.dialogService.showDialog({
          svgIconName: 'b-plus-icon', // Fallback to a FontAwesome icon like 'fas fa-star' if SVG isn't supported here
          iconClass: 'aero-highlight aero-bulb-plus-icon',
          title: this.translation.translateInstant('premium_feature'),
          detail: this.translation.translateInstant(detailCode),
          primaryLabel: this.translation.translateInstant('button_ok'),
          showSecondary: false,
        });

        return; // Abort the API call
      }

      // If they DO have the premium feature, run the Safari check
      this.checkSafariPushNotifications();
    }

    // If they are trying to ENABLE SMS Notifications
    if (channel === 'sms' && key === 'enabled' && willBeEnabled) {
      if (!this.account.features?.SMSFeature?.enabled) {
        if (event?.target) {
          event.target.checked = false;
        }
        this.account.notificationPref.sms.enabled = false;

        const isSchoolDirect = this.authService.currentUser.bulbSchoolDirectMemberships > 0;
        const detailCode = isSchoolDirect
          ? 'notification_feature_check_content_bulbSchoolDirect'
          : 'notification_feature_sms_modal_content';

        this.dialogService.showDialog({
          svgIconName: 'b-plus-icon',
          iconClass: 'aero-highlight aero-bulb-plus-icon',
          title: this.translation.translateInstant('premium_feature'),
          detail: this.translation.translateInstant(detailCode),
          primaryLabel: this.translation.translateInstant('button_ok'),
          showSecondary: false,
        });

        return;
      }
    }

    // Proceed with the standard patch request
    this.account.notificationPref[channel][key] = willBeEnabled;
    this.request('patch', `accounts/${this.account.id}`, {
      notificationPref: { [`${channel}.${key}`]: willBeEnabled },
    });
  }

  /** Updates the frequency of email notifications. */
  setFrequency(value: string): void {
    this.account.notificationPref.email.frequency = value;
    this.request('patch', `accounts/${this.account.id}`, { notificationPref: { 'email.frequency': value } });
  }

  /** Returns ordinary preference keys for a notification channel. */
  notificationKeys(channel: string): string[] {
    const excludedKeys = ['frequency', 'enabled', 'groupNotificationPreferences'];
    return Object.keys(this.account.notificationPref?.[channel] || {})
      .filter((key) => !excludedKeys.includes(key));
  }

  /** Toggles a group-level notification preference and persists the membership. */
  updateGroupNotification(group: any, notification: any, channel: string, value?: boolean): void {
    const enabled = value ?? !group.notificationPrefs[channel][notification.field];
    group.notificationPrefs[channel][notification.field] = enabled;
    const notificationPrefs = {
      ...group.notificationPrefs,
      [channel]: { ...group.notificationPrefs[channel], [notification.field]: enabled },
    };
    this.request('patch', `accounts/${this.account.id}/groupMemberships/${group.groupId}`, { notificationPrefs });
  }

  /** Persists the account's current phone number. */
  updatePhoneNumber(): void {
    this.request('patch', `accounts/${this.account.id}`, { phoneNumber: this.account.phoneNumber });
  }

  /** Opens information about third-party account connections. */
  connectionInfo(): void {
    this.dialogService.showDialog({
      title: this.translation.translateInstant('account_connection_info_title'),
      detail: this.translation.translateInstant('account_connection_info_third_party'),
      primaryLabel: this.translation.translateInstant('button_ok'),
      showSecondary: false,
    });
  }

  /** Returns the best available display identifier for a connection. */
  getConnectionIdentifier = (connection: any): string => {
    if (!connection) return '';
    return this.connectionIdentifierFields.map((field) => connection[field]).find(Boolean) || '';
  };

  /** Opens the parent-account management dialog. */
  showParentAccountsDialog(): void {
    this.dialogService.showDialog({
      icon: 'fas fa-users',
      title: this.translation.translateInstant('account_settings_parent_accounts'),
      customContentComponent: ParentAccountsComponent,
      showFooter: false,
      width: '500px',
      data: {
        account: this.account,
        accountUpdated: (account) => {
          this.account = account;
          this.storedAccount = cloneDeep(account);
          this.authService.setCurrentUser(account);
        },
        reopenDialog: () => {
          this.showParentAccountsDialog();
        },
      },
    });
  }

  /** Opens the dialog for joining a group with an access token. */
  joinGroup(): void {
    let accessToken = '';
    this.dialogService.showDialog({
      icon: 'fas fa-puzzle-piece',
      iconClass: 'aero-highlight',
      customContentComponent: JoinGroupComponent,
      title: this.translation.translateInstant('account_settings_memberships_join'),
      primaryLabel: this.translation.translateInstant('account_settings_memberships_join_text'),
      closeOnPrimary: true,
      data: {
        valueChange: (value) => {
          accessToken = value;
        },
      },
      onPrimary: (dialogRef) => this.submitJoinGroup(accessToken, dialogRef),
    });
  }

  /** Joins a group, refreshes account state, and handles any resulting billing flow. */
  private submitJoinGroup(accessToken: string, dialogRef: any): void {
    this.closeDialog();
    const wasPaidOnJoin = this.authService.currentUser.type !== 'FREE';
    const operation = firstValueFrom(this.accountSettingsService.joinGroup(accessToken))
      .then((resultingAccount: any) => {
        this.account = resultingAccount;
        this.storedAccount = cloneDeep(resultingAccount);
        this.authService.setCurrentUser(resultingAccount);
        dialogRef.close();
        window.location.reload();

        if (!wasPaidOnJoin && this.authService.currentUser.bulbSchoolDirectMemberships > 0) {
          this.stateGoBilling();
        }
      })
      .catch((error) => {
        setTimeout(() => {
          this.dialogService.showDialog({
            title: this.translation.translateInstant('server_error_title'),
            detail: error?.error?.developerMessage
              || this.translation.translateInstant('server_error_body'),
            primaryLabel: this.translation.translateInstant('button_ok'),
            showSecondary: false,
          });
        });
      });
    this.busyService.showBusyDuring(operation);
  }

  /** Opens the promo-code dialog and applies the entered code. */
  applyPromoCode(): void {
    let promoCode = '';
    this.dialogService.showDialog({
      customContentComponent: AddCouponComponent,
      icon: 'fas fa-percent',
      iconClass: 'aero-highlight',
      title: this.translation.translateInstant('account_settings_promo_code'),
      primaryDisabled: true,
      primaryLabel: this.translation.translateInstant('button_apply'),
      dialogInput: {
        inputLabel: this.translation.translateInstant('account_settings_promo_code'),
        onInputChange: (value) => {
          promoCode = value;
        },
      },
      data: {
        position: this.account?.position,
        valueChange: (value: string) => {
          promoCode = value;
        },
      },
      onPrimary: (dialogRef) => {
        const operation = firstValueFrom(this.accountSettingsService.applyPromoCode(promoCode))
          .then((account: any) => {
            this.account = account;
            this.storedAccount = cloneDeep(account);
            this.authService.setCurrentUser(account);
            dialogRef.close();
            this.state.go('account.settings', {
              book: account.position === 'EDUCATOR',
            }, { reload: true });
          })
          .catch((error) => this.errorService.showError(null, error));
        this.busyService.showBusyDuring(operation);
      },
    });
  }

  /** Removes the dynamic dialog host and closes its reference. */
  closeDialog() {
    document.getElementsByTagName('p-dynamicdialog')[0].remove();
    this.dynamicDialogRef.close();
  }

  /** Navigates to the new annual bulbEd Direct billing flow. */
  private stateGoBilling(): void {
    this.state.go('account.billing', {
      mode: 'new',
      period: 'YEAR',
      numberOfAccounts: 1,
      institutionName: 'bulbEdDirect',
      institutionWebsite: ' ',
    });
  }

  /** Redirects the browser to Badgr's OAuth authorization endpoint. */
  redirectToBadgr(): void {
    const badgrUrl = this.appConfig.badgr?.url || 'badgr.com';
    const clientId = this.appConfig.badgr?.clientId || '';

    const ngClientUrl = this.appConfig.ngclient?.url || window.location.origin;
    const redirectUri = encodeURIComponent(`${ngClientUrl}/b/account/settings`);

    if (!clientId) {
      this.dialogService.showDialog({
        title: this.translation.translateInstant('server_error_title'),
        detail: this.translation.translateInstant('server_error_body'),
        primaryLabel: this.translation.translateInstant('button_ok'),
        showSecondary: false,
      });
      return;
    }

    window.location.href = `https://${badgrUrl}/auth/oauth2/authorize`
      + `?client_id=${clientId}`
      + `&redirect_uri=${redirectUri}`
      + '&scope=rw:backpack r:profile';
  }

  /** Sends an account API mutation and synchronizes account state on response. */
  private request(method: string, url: string, body?: any) {
    const hideBusy = this.busyService.showBusy();
    let request;
    if (method === 'patch') {
      request = this.http.patch(url, body);
    } else if (method === 'post') {
      request = this.http.post(url, body);
    } else {
      request = this.http.delete(url, { body });
    }
    return request.pipe(finalize(() => hideBusy())).subscribe((response: any) => {
      if (response?.features) {
        this.account = response;
        this.storedAccount = cloneDeep(response);
        this.authService.setCurrentUser(response);
      }
    });
  }

  /** Indicates whether the signed-in user has the administrator role. */
  get isAdmin(): boolean {
    return this.currentUser?.roles?.includes('ADMIN');
  }

  /** Indicates whether an organization administrator is viewing as this user. */
  get isOrgAdminSwitchUserClient() {
    return this.currentUser?.roles?.includes('SWITCHED_USER');
  }

  /** Opens the MacMillan book dialog when the route and account allow it. */
  private checkMacMillanBookDialog(): void {
    // 1. Check if the 'book' parameter is present in the URL (via UI-Router state)
    const bookParam = this.state?.params?.book;

    // 2. Check if the user is an educator
    const isEducator = this.account?.position === 'EDUCATOR';

    // 3. Retrieve their activated codes
    const codes = this.account?.features?.ActivatedCodesFeature?.codes || [];

    // 4. Check if they have MacMillan creation capabilities
    // (Falling back safely in case this method wasn't perfectly migrated yet)
    const ableToCreateMacMillanGroups = typeof this.authService.isAbleToMakeMacMillanGroups === 'function'
      ? this.authService.isAbleToMakeMacMillanGroups(codes)
      : false;

    // 5. Trigger the dialog if all conditions are met
    if (bookParam && isEducator && ableToCreateMacMillanGroups) {
      // Assuming showMacMillanBookDialog was migrated into your new AeroDialogService
      if (typeof (this.dialogService as any).showMacMillanBookDialog === 'function') {
        (this.dialogService as any).showMacMillanBookDialog(true, true);
      } else {
        this.dialogService.showDialog({
          title: this.translation.translateInstant('server_error_title'),
          detail: this.translation.translateInstant('server_error_body'),
          primaryLabel: this.translation.translateInstant('button_ok'),
          showSecondary: false,
        });
      }
    }
  }

  /** Displays the duplicate-email OAuth error when present in route parameters. */
  private checkDuplicateEmailError(): void {
    // 1. Check if the errorConnected parameter is present and equals 'true'
    const errorConnected = this.state?.params?.errorConnected;

    if (errorConnected === 'true') {
      // 2. Show the error modal
      this.dialogService.showDialog({
        icon: 'fas fa-exclamation-triangle',
        iconClass: 'aero-alert',
        title: this.translation.translateInstant('account_settings_duplicate_email_title'),
        detail: this.translation.translateInstant('account_settings_duplicate_email_body'),
        primaryLabel: this.translation.translateInstant('button_ok'),
        showSecondary: false,
        onPrimary: () => {
          this.clearDuplicateEmailParam();
        },
        onClose: () => {
          this.clearDuplicateEmailParam();
        },
      });
    }
  }

  /** Clears the duplicate-email route state so the error does not reappear. */
  private clearDuplicateEmailParam(): void {
    // Clear the URL parameter so it doesn't trigger again on refresh.
    // Using setTimeout to avoid race conditions with the UI-Router state transition,
    // exactly as the old AngularJS $timeout did.
    setTimeout(() => {
      this.state.go('account.settings', {}, { inherit: false, reload: 'account.settings' });
    });
  }

  /** Enables or refreshes MFA and stores the returned QR-code link. */
  enableOrRefreshMFA(): void {
    if (!(this.account?.connections?.password || this.account?.connections?.mfa)) {
      this.dialogService.showDialog({
        icon: 'fas fa-exclamation-triangle',
        iconClass: 'aero-alert',
        title: this.translation.translateInstant('account_settings_enable_mfa_failed_modal_title'),
        detail: this.translation.translateInstant('account_settings_enable_mfa_failed_modal_description'),
        primaryLabel: this.translation.translateInstant('button_ok'),
        showSecondary: false,
      });
      return;
    }

    const hideBusy = this.busyService.showBusy();
    this.http.post(`accounts/${this.account.id}/connections/multiFactorAuth`, {})
      .pipe(finalize(() => hideBusy()))
      .subscribe({
        next: (response: any) => {
          this.mfaQRCode = response.qrCodeLink;
          this.refreshAccount();
        },
        error: (error) => this.errorService.showError(null, error),
      });
  }

  /** Checks Safari push permission and delegates remote permission handling. */
  private checkSafariPushNotifications(): void {
    const win = window as any;

    // Check whether the browser is Safari and supports push notifications
    if ('safari' in win && 'pushNotification' in win.safari) {

      // Check the browser version via your existing service
      if (this.pushNotificationService.checkBrowserVersion() >= 11) {

        // Grab the webPushId from your AppConfigService (migrated from CONFIG)
        const webPushId = this.appConfig.pushnotification?.apns?.safari?.webPushId;

        if (webPushId) {
          const permissionData = win.safari.pushNotification.permission(webPushId);
          // Pass the permission data and the account object to your service
          this.pushNotificationService.checkRemotePermission(permissionData, this.account);
        }

      } else {
        // Alert for upgrading the browser version
        this.dialogService.showDialog({
          icon: 'fas fa-info-circle',
          iconClass: 'aero-highlight',
          title: this.translation.translateInstant('push_notification_version_upgrade_title'),
          detail: this.translation.translateInstant('push_notification_version_upgrade_content'),
          primaryLabel: this.translation.translateInstant('button_ok'),
          showSecondary: false,
        });
      }
    }
  }

  /** Reloads the account from the API and updates local and authenticated state. */
  private refreshAccount(): void {
    this.http.get(`accounts/${this.account.id}`).subscribe((account: any) => {
      this.account = account;
      this.storedAccount = cloneDeep(account);
      this.authService.setCurrentUser(account);
    });
  }
}
